import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../../identity/types';
import { jobRoleGateway } from '../../index';
import { jobRoleErrorMessage } from '../../jobRoleErrors';
import type { ReferenceCategory, ReferenceOption, ReferenceOptionList } from '../../types';
import type { ComboLoadResult, ComboOption } from './GroupedCombobox';

/**
 * Glue between the reference-options API and the comboboxes. Every option a person can pick comes from the API (EPN standard options and the
 * company's own values), bounded by `take`; nothing here, and nothing in any component, holds an option list of its own.
 */

export const OPTION_TAKE = 50;

export function toComboOption(option: ReferenceOption): ComboOption {
  return {
    value: option.name, label: option.name, detail: option.description, meta: option.custom ? null : option.group, custom: option.custom,
  };
}

export function toComboResult(list: ReferenceOptionList): ComboLoadResult {
  return {
    groups: list.groups.map((g) => ({ key: g.key, label: g.label, options: g.options.map(toComboOption) })),
    notice: list.truncated ? `Showing the first ${list.groups.find((g) => g.key === 'Standard')?.options.length ?? OPTION_TAKE} standard options of ${list.totalStandard}. Type a few letters to narrow the list.` : null,
  };
}

/** Loads one category (optionally with the template whose values should be recommended first). A failure is reported with a plain sentence. */
export function referenceLoader(category: ReferenceCategory, templateId?: string | null) {
  return async (query: string, signal: AbortSignal): Promise<ComboLoadResult> => {
    try {
      const list = await jobRoleGateway.listReferenceOptions(
        { category, take: OPTION_TAKE, ...(query ? { search: query } : {}), ...(templateId ? { templateId } : {}) },
        signal,
      );
      return toComboResult(list);
    } catch (err) {
      if (signal.aborted) throw err;
      throw new Error(jobRoleErrorMessage(err, 'Could not load the options. Try again.'));
    }
  };
}

/**
 * Adds the person's own value to the company's options through the API. A refusal of the value itself (400) is shown; if the list service is simply
 * not available the typed text is still used for this role, so a person is never stuck behind a failed lookup.
 */
export function customCreator(category: ReferenceCategory) {
  return async (name: string): Promise<ComboOption> => {
    try {
      const { option } = await jobRoleGateway.createReferenceOption({ category, name });
      return toComboOption(option);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) throw new Error(jobRoleErrorMessage(err, 'That value cannot be used.'));
      return { value: name, label: name, custom: true };
    }
  };
}

/**
 * Tells whether each stored value is a known option (a standard option name or synonym, or one of the company's own values). A stored value that is
 * not is shown as "custom, kept as entered" and is never dropped or rewritten. `undefined` means the check has not finished.
 */
export function useKnownOptions(category: ReferenceCategory, values: string[]): (value: string) => boolean | undefined {
  const cache = useRef(new Map<string, boolean>());
  const [, setVersion] = useState(0);
  const key = values.join('\u0001');

  useEffect(() => {
    const controller = new AbortController();
    for (const value of values) {
      const k = value.trim().toLowerCase();
      if (!k || cache.current.has(k)) continue;
      jobRoleGateway
        .listReferenceOptions({ category, search: value.trim(), take: 10 }, controller.signal)
        .then((list) => {
          if (controller.signal.aborted) return;
          const known = list.groups.some((g) => g.options.some((o) => o.name.toLowerCase() === k || o.synonyms.some((s) => s.toLowerCase() === k)));
          cache.current.set(k, known);
          setVersion((n) => n + 1);
        })
        .catch(() => { /* unknown stays unknown: no marker is shown */ });
    }
    return () => controller.abort();
    // `values` is read through `key`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, key]);

  return useCallback((value: string) => cache.current.get(value.trim().toLowerCase()), []);
}
